import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Tooltip,
	Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { useMemo, useState } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useDispatch, useSelector } from "react-redux";

import { LoaderWithOverlay } from "components/atoms";

import {
	getDashboardSelectedYear,
	getDashboardYearReport,
	getDashboardYearReportData,
	setDashboardReportYear,
} from "state/modules/dashboard";
import { PropertyYearReport } from "../../interfaces/reports";
import { MAIN_THEME } from "styles/colors";

const Title = styled.p`
	font-family: "Open Sans Medium", sans-serif;
	font-size: 22px;
	font-weight: bold;
	margin: 0 10px;
	color: ${MAIN_THEME.TEXT_PRIMARY};
`;

const Nav = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px;

	@media (max-width: 480px) {
		gap: 4px;
	}
`;

const NavButton = styled.button`
	background: transparent;
	outline: none;
	cursor: pointer;
	border: none;
	font-weight: bold;
	width: 20px;
	height: 20px;
	display: flex;
	justify-content: center;
	align-items: center;
	color: ${MAIN_THEME.TEXT_PRIMARY};
`;

const NavTitle = styled.p`
	margin: 0 10px;
	color: ${MAIN_THEME.TEXT_SECONDARY};
`;

const Header = styled.div`
	margin-bottom: 20px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const HeaderFirstRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const HeaderSecondRow = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
`;

const ChartContainer = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const Container = styled.div`
	width: 100%;
	background: transparent;
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	padding: 24px;
	border-radius: 12px;
	position: relative;
	max-height: 600px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;

interface DatasetButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	isActive: boolean;
	color: string;
}

const DatasetButton = styled.button<DatasetButtonProps>`
	background: ${({ isActive, color }) =>
		isActive
			? `${MAIN_THEME.SECONDARY}`
			: MAIN_THEME.DARK_BG_SECONDARY};
	color: ${({ isActive }) =>
		(isActive ? MAIN_THEME.DARK_BG : MAIN_THEME.TEXT_PRIMARY)};
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	padding: 6px 12px;
	margin-right: 5px;
	margin-bottom: 5px;
	border-radius: 8px;
	cursor: pointer;
	outline: none;
	font-size: 12px;
	font-weight: 600;
	transition: all 0.2s ease;

	@media (max-width: 768px) {
		padding: 5px 10px;
		font-size: 11px;
		margin-right: 4px;
		margin-bottom: 4px;
	}

	@media (max-width: 480px) {
		padding: 4px 8px;
		font-size: 10px;
		margin-right: 3px;
		margin-bottom: 3px;
	}

	&:hover {
		border-color: ${MAIN_THEME.SECONDARY};
		color: ${({ isActive }) => (isActive ? MAIN_THEME.DARK_BG : MAIN_THEME.SECONDARY)};
	}
`;

const ButtonsContainer = styled.div`
	display: flex;
	align-items: center;
	margin-top: 12px;
`;

const YearNavButton = styled.button`
	background: ${MAIN_THEME.DARK_BG_SECONDARY};
	color: ${MAIN_THEME.TEXT_PRIMARY};
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	padding: 5px 10px;
	margin-right: 5px;
	border-radius: 8px;
	cursor: pointer;
	outline: none;
	transition: all 0.2s ease;
	font-size: 16px;

	@media (max-width: 768px) {
		padding: 4px 8px;
		font-size: 14px;
	}

	@media (max-width: 480px) {
		padding: 3px 6px;
		font-size: 12px;
		margin-right: 3px;
	}

	&:hover {
		border-color: ${MAIN_THEME.SECONDARY};
		color: ${MAIN_THEME.SECONDARY};
	}
`;

const YearLabel = styled.span`
	font-family: "DS-Digital", sans-serif;
	font-size: 24px;
	font-weight: bold;
	margin: 0 10px;
	color: ${MAIN_THEME.TEXT_PRIMARY};

	@media (max-width: 768px) {
		font-size: 20px;
		margin: 0 8px;
	}

	@media (max-width: 480px) {
		font-size: 18px;
		margin: 0 6px;
	}
`;
ChartJS.register(
	CategoryScale,
	LinearScale,
	BarElement,
	Tooltip,
	Legend,
	ChartDataLabels
);

const labels = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

interface Props {
	isLoading: boolean;
}

const DashboardIncomeChart = (props: Props) => {
	const { isLoading } = props;

	const dispatch = useDispatch();

	const handleChangeYear = (selectedYear: number) => {
		dispatch(setDashboardReportYear(selectedYear));
		dispatch(getDashboardYearReport());
	};

	const year = useSelector(getDashboardSelectedYear);
	const report: PropertyYearReport | null = useSelector(
		getDashboardYearReportData
	);

	const CORAL_COLOR = "#ff6161";

	const datasetsInfo = [
		{
			id: "income",
			label: "Income",
			color: CORAL_COLOR,
			unit: "$",
			getData: (data: PropertyYearReport | null) =>
				(data?.yearData || []).map(
					(item) => item.calculations.totalInCurrency.totalIncomeInCurrency
				),
		},

		{
			id: "payouts",
			label: "Payouts",
			color: CORAL_COLOR,
			unit: "$",
			getData: (data: PropertyYearReport | null) =>
				(data?.yearData || []).map(
					(item) =>
						item.calculations.totalInCurrency.totalIncomeInCurrency -
						item.calculations.totalInCurrency.totalExpensesInCurrency
				),
		},

		{
			id: "expenses",
			label: "Expenses",
			color: CORAL_COLOR,
			unit: "$",
			getData: (data: PropertyYearReport | null) =>
				(data?.yearData || []).map(
					(item) => item.calculations.totalInCurrency.totalExpensesInCurrency
				),
		},
		{
			id: "cleaning",
			label: "Cleaning",
			color: CORAL_COLOR,
			unit: "$",
			getData: (data: PropertyYearReport | null) =>
				(data?.yearData || []).map(
					(item) => item.calculations.totalInCurrency.totalCleaningInCurrency
				),
		},
		{
			id: "adr",
			label: "ADR",
			color: CORAL_COLOR,
			unit: "$",
			getData: (data: PropertyYearReport | null) =>
				(data?.yearData || []).map(
					(item) => item.calculations.totalInCurrency.adr
				),
		},
		{
			id: "revpar",
			label: "RevPAR",
			color: CORAL_COLOR,
			unit: "$",
			getData: (data: PropertyYearReport | null) =>
				(data?.yearData || []).map(
					(item) => item.calculations.totalInCurrency.revPar
				),
		},
		{
			id: "occupancy",
			label: "Occupancy",
			color: CORAL_COLOR,
			unit: "%",
			getData: (data: PropertyYearReport | null) =>
				(data?.yearData || []).map((item) => item.calculations.occupancyRate),
		},
		{
			id: "alos",
			label: "ALOS",
			color: CORAL_COLOR,
			unit: "days",
			getData: (data: PropertyYearReport | null) =>
				(data?.yearData || []).map((item) => item.calculations.alos),
		},
	];

	const [selectedDataset, setSelectedDataset] = useState(datasetsInfo[0].id);

	// Define the datasets and their corresponding colors

	const data = useMemo(() => {
		const selectedData = datasetsInfo.find(
			(dataSetInfo) => dataSetInfo.id === selectedDataset
		);
		return {
			labels,
			datasets: [
				{
					data: selectedData?.getData(report),
					backgroundColor: selectedData?.color,
				},
			],
		};
	}, [selectedDataset, report]);

	const handleDatasetButtonClick = (datasetId: string) => {
		setSelectedDataset(datasetId);
	};

	const formatValue = (value: number, kpiId: string) => {
		const selectedData = datasetsInfo.find(
			(dataSetInfo) => dataSetInfo.id === kpiId
		);
		const unit = selectedData ? selectedData.unit : "";
		
		if (value === 0 || !value) return "0" + unit;
		
		// Format number with thousands separator
		const formattedNumber = Math.round(value).toLocaleString();
		return `${formattedNumber}${unit}`;
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		scales: {
			x: {
				grid: {
					display: false,
				},
				ticks: {
					color: MAIN_THEME.TEXT_TERTIARY,
				},
			},
			y: {
				grid: {
					color: 'rgba(240, 244, 255, 0.08)',
				},
				ticks: {
					color: MAIN_THEME.TEXT_TERTIARY,
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				enabled: true,
				backgroundColor: 'rgba(15, 23, 42, 0.9)',
				titleColor: MAIN_THEME.TEXT_PRIMARY,
				bodyColor: MAIN_THEME.TEXT_PRIMARY,
				borderColor: 'rgba(148, 163, 184, 0.25)',
				borderWidth: 1,
				titleFont: {
					weight: 'bold' as const,
					size: 12,
				},
				bodyFont: {
					size: 12,
				},
				padding: 10,
				displayColors: false,
				callbacks: {
					label(context: any) {
						return formatValue(context.parsed.y, selectedDataset);
					},
				},
			},
			ChartDataLabels,
			datalabels: {
				anchor: 'end' as const,
				align: 'top' as const,
				offset: 4,
				formatter(value: number) {
					return formatValue(value, selectedDataset);
				},
				color: MAIN_THEME.TEXT_PRIMARY,
				font: {
					weight: 'bold' as const,
					size: 11,
					family: 'Open Sans, sans-serif',
				},
				clamp: false,
				clip: false,
			},
		},
	};

	return (
		<Container>
			<Header>
				<HeaderFirstRow>
					<Nav>
						<YearNavButton
							onClick={() => handleChangeYear(year - 1)}
							disabled={isLoading}
						>
							{"\u{2190}"} {/* Left arrow symbol */}
						</YearNavButton>
						<YearLabel>{year}</YearLabel>
						<YearNavButton
							disabled={isLoading}
							onClick={() => handleChangeYear(year + 1)}
						>
							{"\u{2192}"} {/* Right arrow symbol */}
						</YearNavButton>
					</Nav>
					<Title>
						{
							datasetsInfo.find((dataset) => dataset.id === selectedDataset)
								?.label
						}{" "}
						for {year}
					</Title>
				</HeaderFirstRow>
				<HeaderSecondRow>
					{datasetsInfo.map((dataSetInfo) => (
						<DatasetButton
							key={dataSetInfo.id}
							onClick={() => handleDatasetButtonClick(dataSetInfo.id)}
							isActive={selectedDataset === dataSetInfo.id}
							color={dataSetInfo.color}
						>
							{dataSetInfo.label}
						</DatasetButton>
					))}
				</HeaderSecondRow>
			</Header>

			<ChartContainer>
				<Bar options={options} data={data} />
			</ChartContainer>
			{isLoading ? <LoaderWithOverlay /> : null}
		</Container>
	);
};

export default DashboardIncomeChart;
